import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";

/**
 * DiagramEditModal — Proper Flow Canvas
 *
 * Fan-out  (1 → N):  Source node appears ONCE on left with a trunk line
 *                    branching to each destination on the right.
 *                    Each branch has its own field-mapping rows + Add button.
 * Fan-in   (N → 1):  Each source on the left, trunk merges into one destination.
 *                    Each branch has its own field-mapping rows + Add button.
 * 1:1 / 3-node:      Simple straight connection with field chips between nodes.
 */

const ALL_TRIGGERS = [
  { value: "Http Trigger", category: "Standard" },
  { value: "Webhook Trigger", category: "Standard" },
  { value: "Hubspot Trigger", category: "CRM" },
  { value: "Salesforce Platform Event", category: "CRM" },
  { value: "AWS SNS Trigger", category: "AWS" },
  { value: "AWS SQS Trigger", category: "AWS" },
  { value: "Azure Service Bus Trigger", category: "Azure" },
  { value: "Websocket Trigger", category: "Standard" },
  { value: "Timer Trigger", category: "Standard" },
  { value: "GraphQL Subscription", category: "API" },
  { value: "gRPC Trigger", category: "API" },
  { value: "Google Pub/Sub", category: "GCP" },
  { value: "Kafka Trigger", category: "Streaming" },
  { value: "Redis Pub/Sub", category: "Database" },
  { value: "Azure Event Grid", category: "Azure" },
  { value: "Blob Storage Trigger", category: "Azure" },
  { value: "Shopify Webhook", category: "E-commerce" },
  { value: "Stripe Webhook", category: "Finance" },
  { value: "Slack Event Trigger", category: "Communication" },
];
const ALL_CONNECTORS = [
  { value: "Rest API", category: "API" },
  { value: "Hubspot", category: "CRM" },
  { value: "OData", category: "Database" },
  { value: "GraphQL", category: "API" },
  { value: "DB Query", category: "Database" },
  { value: "Salesforce", category: "CRM" },
  { value: "ServiceNow", category: "ITSM" },
  { value: "Workday", category: "HR" },
  { value: "SAP", category: "Enterprise" },
  { value: "SOAP API", category: "API" },
  { value: "AWS S3", category: "AWS" },
  { value: "Snowflake", category: "Data Warehouse" },
  { value: "Zoho CRM", category: "CRM" },
  { value: "Shopify", category: "E-commerce" },
  { value: "Google BigQuery", category: "Data Warehouse" },
  { value: "Not Required", category: "Standard" },
];
const ROLE_OPTIONS = { trigger: ALL_TRIGGERS, source: ALL_CONNECTORS, destination: ALL_CONNECTORS, action: ALL_CONNECTORS };

function normalizeMapping(m) {
  return {
    source_label: m.source_label || m.source_field || m.source || m.from || m.input || "",
    destination_label: m.destination_label || m.dest_field || m.dest || m.to || m.output || "",
    transformation: m.transformation || m.transform || "",
  };
}

const PlatformPicker = ({ role = "destination", currentValue, onSelect, onClose }) => {
  const ref = useRef();
  
  const grouped = {
    "Triggers": ALL_TRIGGERS,
    "Connectors": ALL_CONNECTORS
  };

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [onClose]);

  return (
    <div ref={ref} className="absolute z-[80] bg-white rounded-xl overflow-hidden"
      style={{
        top: "calc(100% + 4px)", left: 0, width: 240,
        boxShadow: "0 8px 30px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.07)"
      }}>
      <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
        <p className="text-[0.65rem] font-black uppercase tracking-widest text-gray-500 capitalize">{role} Platform</p>
      </div>
      <div className="max-h-52 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
        {Object.entries(grouped).map(([cat, opts]) => (
          <div key={cat}>
            <div className="px-4 py-1.5 text-[0.58rem] font-black text-gray-300 uppercase tracking-widest bg-gray-50/70 border-b border-gray-100">{cat}</div>
            {opts.map((o) => (
              <button key={o.value} onClick={() => { onSelect(o.value); onClose(); }}
                className={`w-full text-left px-4 py-2 border-b border-gray-50 transition ${o.value === currentValue ? "bg-blue-50" : "hover:bg-gray-50"}`}>
                <p className={`text-[0.8rem] font-bold ${o.value === currentValue ? "text-blue-700" : "text-gray-800"}`}>{o.value}</p>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── SVG Layout constants ─────────────────────────────────────────────────────
const SVG_W = 1040;
const NODE_W = 140;
const NODE_H = 40;
const CHIP_W = 148;
const CHIP_H = 28;
const CHIP_GAP = 10;
const ROW_H = CHIP_H + CHIP_GAP;

const COL_TRIG_X = 16;
const COL_L_X = 176;    // left node x
const CHIP_L_X = 360;   // left chips x
const CHIP_R_X = 656;   // right chips x
const COL_R_X = 866;   // right node x

const HEADER_H = 36;    // top header row height
const S_PAD_T = 18;    // section padding top (above first chip row)
const S_PAD_B = 12;    // section padding bottom (below add btn)
const ADD_H = 28;    // + Add button height
const SEC_GAP = 22;    // gap between sections (contains divider)
const TRUNK_X_L = COL_L_X + NODE_W + 22;   // fan-out trunk x (right of left node)
const TRUNK_X_R = COL_R_X - 22;            // fan-in  trunk x (left of right node)

// Node visual colours
const NODE_COLOR_MAP = {
  trigger: { fill: "#ffffff", border: "#23346e", text: "#23346e", badge: "TRIGGER" },
  source: { fill: "#ffffff", border: "#23346e", text: "#23346e", badge: "SOURCE" },
  destination: { fill: "#ffffff", border: "#23346e", text: "#23346e", badge: "DESTINATION" },
  action: { fill: "#ffffff", border: "#23346e", text: "#23346e", badge: "ACTION" },
};

function sectionHeight(nRows) {
  // Always reserve space for at least the add button + padding
  return S_PAD_T + Math.max(nRows, 0) * ROW_H + ADD_H + S_PAD_B;
}

// ─── Inline SVG node box ──────────────────────────────────────────────────────
const NodeBox = ({ x, y, label, nodeType = "destination", onEdit }) => {
  const c = NODE_COLOR_MAP[nodeType] || NODE_COLOR_MAP.destination;
  return (
    <g>
      {/* Outer shadow rect */}
      <rect x={x + 2} y={y + 3} width={NODE_W} height={NODE_H} rx={8} fill="rgba(0,0,0,0.06)" />
      {/* Main rect */}
      <rect x={x} y={y} width={NODE_W} height={NODE_H} rx={8} fill={c.fill} stroke={c.border} strokeWidth="2" />
      {/* Badge label */}
      <text x={x + 10} y={y + 11} fontSize="7" fontWeight="900"
        fill={c.text} fillOpacity="0.65" letterSpacing="1.2"
        fontFamily="ui-sans-serif, system-ui, sans-serif">
        {c.badge}
      </text>
      {/* Platform name */}
      <text x={x + 10} y={y + NODE_H - 10} fontSize="13" fontWeight="700" fill={c.text}
        fontFamily="ui-sans-serif, system-ui, sans-serif">
        {label}
      </text>
      {/* Edit pencil icon button */}
      <g style={{ cursor: "pointer" }} onClick={(e) => onEdit(e)}>
        <rect x={x + NODE_W - 24} y={y + 5} width={18} height={14} rx={4} fill="rgba(35,52,110,0.1)" />
        <text x={x + NODE_W - 15} y={y + 15.5} textAnchor="middle" fontSize="9.5"
          fill="#23346e"
          fontFamily="ui-sans-serif, system-ui, sans-serif">✎</text>
      </g>
    </g>
  );
};

// ─── Flow Canvas SVG ──────────────────────────────────────────────────────────
/**
 * branchMappings: array of arrays — one per section/branch.
 * For 1:1 there is 1 branch.  Fan-out/Fan-in each branch maps independently.
 */
const FlowCanvas = ({
  platformNodes,
  layoutEdges,          // [{from, to}] representing native structure
  branchMappings,       // string[][]: per-branch mappings array
  onMappingChange,      // (branchIdx, rowIdx, field, value)
  onDeleteMapping,      // (branchIdx, rowIdx)
  onAddMapping,         // (branchIdx)
  onNodeChange,
}) => {
  const [pickerState, setPickerState] = useState(null); // { id, x, y }

  const TYPE_ORDER = { trigger: 0, source: 1, action: 2, destination: 3 };
  const sorted = useMemo(() =>
    [...platformNodes].sort((a, b) => (TYPE_ORDER[a.type] ?? 9) - (TYPE_ORDER[b.type] ?? 9)),
    [platformNodes]);

  const leftNodes = sorted.filter((n) => n.type !== "destination");
  const rightNodes = sorted.filter((n) => n.type === "destination");

  const lNodes = leftNodes.length > 0 ? leftNodes : [{ id: "_src", type: "source", label: "Source" }];
  const rNodes = rightNodes.length > 0 ? rightNodes : [{ id: "_dst", type: "destination", label: "Destination" }];

  let isFanOut = false;
  let isFanIn = false;
  let isPipeline = false;

  if (layoutEdges) {
    const inDeg = {}, outDeg = {};
    layoutEdges.forEach(e => {
       outDeg[e.from] = (outDeg[e.from] || 0) + 1;
       inDeg[e.to] = (inDeg[e.to] || 0) + 1;
    });
    const maxOut = Math.max(0, ...Object.values(outDeg));
    const maxIn = Math.max(0, ...Object.values(inDeg));
    if (maxOut > 1) isFanOut = true;
    else if (maxIn > 1) isFanIn = true;
    else if (layoutEdges.length > 1) isPipeline = true;
  } else {
    isFanOut = lNodes.length === 1 && rNodes.length > 1;
    isFanIn = lNodes.length > 1 && rNodes.length === 1;
  }

  const isSequential1to1 = !isPipeline && lNodes.length === 2 && lNodes[0].type === "trigger" && (lNodes[1].type === "source" || lNodes[1].type === "action") && rNodes.length === 1 && branchMappings.length === 1;

  if (isSequential1to1) {
    isFanOut = false;
    isFanIn = false;
  }

  const numSections = isPipeline ? layoutEdges.length : (isSequential1to1 ? 1 : Math.max(lNodes.length, rNodes.length));

  const sections = useMemo(() => {
    let y = HEADER_H;
    return Array.from({ length: numSections }, (_, i) => {
      const secMaps = (branchMappings[i] || []);
      const h = sectionHeight(secMaps.length);
      
      let ln, rn;
      if (isPipeline && layoutEdges[i]) {
        ln = platformNodes.find(n => n.id === layoutEdges[i].from) || lNodes[0];
        rn = platformNodes.find(n => n.id === layoutEdges[i].to) || rNodes[0];
      } else {
        ln = lNodes[Math.min(i, lNodes.length - 1)];
        rn = rNodes[Math.min(i, rNodes.length - 1)];
      }

      const s = { sIdx: i, startY: y, h, mappings: secMaps, ln, rn };
      y += h + (i < numSections - 1 ? SEC_GAP : 0);
      return s;
    });
  }, [branchMappings, lNodes, rNodes, numSections, layoutEdges, platformNodes, isPipeline]);

  const totalH = sections.reduce((acc, s) => Math.max(acc, s.startY + s.h), 0) + 30;
  const svgH = Math.max(totalH, 240);

  // For fan-out: source node vertically centered across all sections
  const allSec = sections;
  const srcCY = isFanOut
    ? (allSec[0].startY + S_PAD_T + allSec[allSec.length - 1].startY + allSec[allSec.length - 1].h - S_PAD_B) / 2
    : sections[0]
      ? sections[0].startY + S_PAD_T + (sections[0].mappings.length > 0 ? (sections[0].mappings.length - 1) * ROW_H / 2 + CHIP_H / 2 : CHIP_H / 2)
      : HEADER_H + NODE_H / 2;
  const srcNodeY = srcCY - NODE_H / 2;

  // For fan-in: dest node vertically centered across all sections
  const dstCY = isFanIn
    ? (allSec[0].startY + S_PAD_T + allSec[allSec.length - 1].startY + allSec[allSec.length - 1].h - S_PAD_B) / 2
    : sections[0]
      ? sections[0].startY + S_PAD_T + (sections[0].mappings.length > 0 ? (sections[0].mappings.length - 1) * ROW_H / 2 + CHIP_H / 2 : CHIP_H / 2)
      : HEADER_H + NODE_H / 2;
  const dstNodeY = dstCY - NODE_H / 2;

  // trunk line extents
  const trunkTopY = sections[0]?.startY + S_PAD_T + CHIP_H / 2;
  const lastSec = sections[sections.length - 1];
  const trunkBottomY = lastSec
    ? lastSec.startY + S_PAD_T + Math.max(lastSec.mappings.length - 1, 0) * ROW_H + CHIP_H / 2
    : trunkTopY;

  const handleEditClick = (id, e) => {
    if (pickerState?.id === id) setPickerState(null);
    else setPickerState({ id, x: e.clientX, y: e.clientY });
  };

  const viewLeft = isSequential1to1 ? 0 : 160;
  const viewW = SVG_W - viewLeft;
  const viewCenter = viewLeft + viewW / 2;

  return (
    <div className="relative w-full">
      <svg viewBox={`${viewLeft} 0 ${viewW} ${svgH}`}
        style={{ display: "block", width: "100%", height: "auto", overflow: "visible" }}>
        <defs>
          <marker id="arr" markerWidth="7" markerHeight="5" refX="6" refY="2.5" orient="auto">
            <polygon points="0 0, 7 2.5, 0 5" fill="#374151" />
          </marker>
        </defs>

        {/* ── Column headers ── */}
        {[
          ...(isSequential1to1 ? [[COL_TRIG_X + NODE_W / 2, "Trigger"]] : []),
          [COL_L_X + NODE_W / 2, isSequential1to1 ? "Source" : "Source / Trigger"],
          [CHIP_L_X + CHIP_W / 2, "Source Fields"],
          [CHIP_R_X + CHIP_W / 2, "Destination Fields"],
          [COL_R_X + NODE_W / 2, "Destination"],
        ].map(([x, label]) => (
          <text key={label} x={x} y={22} textAnchor="middle" fontSize="11" fontWeight="800"
            fill="#1f2937" letterSpacing="0.8" fontFamily="ui-sans-serif, system-ui, sans-serif">
            {label}
          </text>
        ))}

        {/* ══ FAN-OUT: Source node appears ONCE, centered, trunk branches right ══ */}
        {isFanOut && (
          <g>
            {/* Single source node, vertically centered */}
            <NodeBox x={COL_L_X} y={srcNodeY} label={lNodes[0].label} nodeType={lNodes[0].type}
              onEdit={(e) => handleEditClick(lNodes[0].id, e)} />

            {/* Horizontal connector from source right edge to trunk */}
            <line x1={COL_L_X + NODE_W} y1={srcCY} x2={TRUNK_X_L} y2={srcCY}
              stroke="#6b7280" strokeWidth="2" />

            {/* Vertical trunk line through all branch rows */}
            {sections.length > 1 && (
              <line x1={TRUNK_X_L} y1={trunkTopY} x2={TRUNK_X_L} y2={trunkBottomY}
                stroke="#6b7280" strokeWidth="2" />
            )}
          </g>
        )}

        {/* ══ FAN-IN: Destination node appears ONCE, centered, trunk merges from left ══ */}
        {isFanIn && (
          <g>
            {/* Single destination node, vertically centered */}
            <NodeBox x={COL_R_X} y={dstNodeY} label={rNodes[0].label} nodeType={rNodes[0].type}
              onEdit={(e) => handleEditClick(rNodes[0].id, e)} />

            {/* Horizontal connector from trunk to dest left edge */}
            <line x1={TRUNK_X_R} y1={dstCY} x2={COL_R_X} y2={dstCY}
              stroke="#6b7280" strokeWidth="2" />

            {/* Vertical trunk line through all branch rows */}
            {sections.length > 1 && (
              <line x1={TRUNK_X_R} y1={trunkTopY} x2={TRUNK_X_R} y2={trunkBottomY}
                stroke="#6b7280" strokeWidth="2" />
            )}
          </g>
        )}

        {/* ══ All Sections (branches) ══ */}
        {sections.map((sec, si) => {
          const { ln, rn, startY, h, mappings: secMaps } = sec;
          const numRows = secMaps.length;

          // Fan-out: left node drawn once above — per-section only draw right node
          // Fan-in:  right node drawn once above — per-section only draw left node
          const showLeftNode = !isFanOut;
          const showRightNode = !isFanIn;

          // Y center of chip area for this section
          const chipAreaTopY = startY + S_PAD_T;
          const chipsCenterY = chipAreaTopY + (numRows > 0 ? (numRows - 1) * ROW_H / 2 + CHIP_H / 2 : CHIP_H / 2);

          const nodeY = isPipeline || (!isFanOut && !isFanIn)
             ? chipsCenterY - NODE_H / 2
             : (numRows > 0 ? chipAreaTopY + (numRows - 1) * ROW_H / 2 : chipAreaTopY);

          // Connection point on the "per-section" side
          const secCY = isFanIn ? chipsCenterY : nodeY + NODE_H / 2;

          const fanOutBranchX = TRUNK_X_L;
          const fanInBranchX = TRUNK_X_R;

          return (
            <g key={si}>
              {/* Section divider + branch label for sections 2+ */}
              {si > 0 && (
                <g>
                  <line x1={viewLeft + 20} y1={startY - SEC_GAP / 2} x2={SVG_W - 20} y2={startY - SEC_GAP / 2}
                    stroke="#e5e7eb" strokeWidth="1" strokeDasharray="5 4" />
                  {(isPipeline || isFanOut || isFanIn) && (
                    <>
                      <rect
                        x={viewCenter - 72} y={startY - SEC_GAP / 2 - 9}
                        width={144} height={18} rx={9}
                        fill="#f0fdf4" stroke="#bbf7d0" strokeWidth="1" />
                      <text x={viewCenter} y={startY - SEC_GAP / 2 + 4.5}
                        textAnchor="middle" fontSize="8.5" fill="#15803d" fontWeight="700"
                        fontFamily="ui-sans-serif, system-ui, sans-serif">
                        {isFanOut ? `Leg ${si + 1} → ${rn.label}` : isFanIn ? `Leg ${si + 1} ← ${ln.label}` : `Leg ${si + 1}: ${ln.label} → ${rn.label}`}
                      </text>
                    </>
                  )}
                </g>
              )}
              {/* For FAN-OUT/PIPELINE section 0: add branch label too */}
              {si === 0 && (isPipeline || isFanOut || isFanIn) && (
                <text x={viewCenter} y={startY + 10}
                  textAnchor="middle" fontSize="8.5" fill="#15803d" fontWeight="700"
                  fontFamily="ui-sans-serif, system-ui, sans-serif">
                  {isFanOut ? `Leg 1 → ${rn.label}` : isFanIn ? `Leg 1 ← ${ln.label}` : `Leg 1: ${ln.label} → ${rn.label}`}
                </text>
              )}

              {/* Left node (shown per-section for 1:1 and fan-in only) */}
              {showLeftNode && (
                isSequential1to1 ? (
                  <g>
                    <NodeBox x={COL_TRIG_X} y={nodeY} label={lNodes[0].label} nodeType={lNodes[0].type}
                      onEdit={(e) => handleEditClick(lNodes[0].id, e)} />
                    <path d={`M ${COL_TRIG_X + NODE_W} ${nodeY + NODE_H / 2} L ${COL_L_X - 6} ${nodeY + NODE_H / 2}`}
                      fill="none" stroke="#6b7280" strokeWidth="1.5" markerEnd="url(#arr)" />
                    <NodeBox x={COL_L_X} y={nodeY} label={lNodes[1].label} nodeType={lNodes[1].type}
                      onEdit={(e) => handleEditClick(lNodes[1].id, e)} />
                  </g>
                ) : (
                  <NodeBox x={COL_L_X} y={nodeY} label={ln.label} nodeType={ln.type}
                    onEdit={(e) => handleEditClick(ln.id, e)} />
                )
              )}

              {/* Right node (shown per-section for 1:1 and fan-out only) */}
              {showRightNode && (
                <NodeBox x={COL_R_X} y={nodeY} label={rn.label} nodeType={rn.type}
                  onEdit={(e) => handleEditClick(rn.id, e)} />
              )}

              {/* ── Mapping rows ── */}
              {numRows === 0 ? (
                /* Ghost "no mappings" placeholder */
                <g>
                  {/* Left connector */}
                  {isFanOut ? (
                    <line x1={fanOutBranchX} y1={secCY} x2={CHIP_L_X} y2={secCY}
                      stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="4 3" />
                  ) : (
                    <line x1={COL_L_X + NODE_W} y1={secCY} x2={CHIP_L_X} y2={secCY}
                      stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="4 3" />
                  )}
                  {/* Ghost placeholder bar */}
                  <rect x={CHIP_L_X} y={chipAreaTopY} width={CHIP_R_X + CHIP_W - CHIP_L_X} height={CHIP_H}
                    rx={CHIP_H / 2} fill="#f9fafb" stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4 3" />
                  <text x={(CHIP_L_X + CHIP_R_X + CHIP_W) / 2} y={chipAreaTopY + CHIP_H / 2 + 4}
                    textAnchor="middle" fontSize="9.5" fill="#1f2937"
                    fontFamily="ui-sans-serif, system-ui, sans-serif">
                    No field mappings — click + Add below
                  </text>
                  {/* Right connector */}
                  {isFanIn ? (
                    <line x1={CHIP_R_X + CHIP_W} y1={secCY} x2={fanInBranchX} y2={secCY}
                      stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="4 3" />
                  ) : (
                    <line x1={CHIP_R_X + CHIP_W} y1={secCY} x2={COL_R_X} y2={secCY}
                      stroke="#d1d5db" strokeWidth="1.5" strokeDasharray="4 3" />
                  )}
                </g>
              ) : (
                secMaps.map((m, fi) => {
                  const ry = chipAreaTopY + fi * ROW_H;
                  const cy = ry + CHIP_H / 2;
                  const mid = (CHIP_L_X + CHIP_W + CHIP_R_X) / 2;

                  return (
                    <g key={fi}>
                      {/* Left branch: trunk/node → left chip */}
                      {isFanOut ? (
                        // From trunk point — horizontal branch, then curve to chip row
                        numRows > 1 ? (
                          <path d={`M ${fanOutBranchX} ${secCY} H ${fanOutBranchX + 12} C ${CHIP_L_X - 20} ${secCY}, ${CHIP_L_X - 10} ${cy}, ${CHIP_L_X} ${cy}`}
                            fill="none" stroke="#6b7280" strokeWidth="1.5" />
                        ) : (
                          <line x1={fanOutBranchX} y1={cy} x2={CHIP_L_X} y2={cy}
                            stroke="#6b7280" strokeWidth="1.5" />
                        )
                      ) : (
                        <path d={`M ${COL_L_X + NODE_W} ${secCY} C ${CHIP_L_X - 30} ${secCY}, ${CHIP_L_X - 15} ${cy}, ${CHIP_L_X} ${cy}`}
                          fill="none" stroke="#6b7280" strokeWidth="1.5" />
                      )}
                      <circle cx={CHIP_L_X} cy={cy} r={3.5} fill="#6b7280" />

                      {/* Right branch: right chip → trunk/node */}
                      {isFanIn ? (
                        numRows > 1 ? (
                          <path d={`M ${CHIP_R_X + CHIP_W} ${cy} C ${fanInBranchX + 15} ${cy}, ${fanInBranchX + 10} ${secCY}, ${fanInBranchX} ${secCY}`}
                            fill="none" stroke="#6b7280" strokeWidth="1.5" />
                        ) : (
                          <line x1={CHIP_R_X + CHIP_W} y1={cy} x2={fanInBranchX} y2={cy}
                            stroke="#6b7280" strokeWidth="1.5" />
                        )
                      ) : (
                        <path d={`M ${CHIP_R_X + CHIP_W} ${cy} C ${COL_R_X - 20} ${cy}, ${COL_R_X - 10} ${secCY}, ${COL_R_X} ${secCY}`}
                          fill="none" stroke="#6b7280" strokeWidth="1.5" />
                      )}
                      <circle cx={CHIP_R_X + CHIP_W} cy={cy} r={3.5} fill="#6b7280" />

                      {/* Main bezier mapping arrow between chips */}
                      <path d={`M ${CHIP_L_X + CHIP_W} ${cy} C ${mid} ${cy - 24}, ${mid} ${cy - 24}, ${CHIP_R_X} ${cy}`}
                        fill="none" stroke="#374151" strokeWidth="1.5"
                        strokeDasharray="6 4" markerEnd="url(#arr)" />

                      {/* Source chip */}
                      <rect x={CHIP_L_X} y={ry} width={CHIP_W} height={CHIP_H} rx={6}
                        fill="white" stroke={m.source_label ? "#93c5fd" : "#e5e7eb"} strokeWidth="1.5" />
                      <foreignObject x={CHIP_L_X + 2} y={ry + 2} width={CHIP_W - 4} height={CHIP_H - 4}>
                        <input xmlns="http://www.w3.org/1999/xhtml"
                          value={m.source_label}
                          onChange={(e) => onMappingChange(si, fi, "source_label", e.target.value)}
                          placeholder="source.field"
                          style={{
                            width: "100%", height: "100%", border: "none", outline: "none",
                            background: "transparent", fontSize: 11, fontWeight: 700, color: "#111827",
                            fontFamily: "ui-monospace, SFMono-Regular, monospace",
                            paddingLeft: 8, paddingRight: 4, boxSizing: "border-box"
                          }} />
                      </foreignObject>

                      {/* Destination chip */}
                      <rect x={CHIP_R_X} y={ry} width={CHIP_W} height={CHIP_H} rx={6}
                        fill="white" stroke={m.destination_label ? "#86efac" : "#e5e7eb"} strokeWidth="1.5" />
                      <foreignObject x={CHIP_R_X + 2} y={ry + 2} width={CHIP_W - 4} height={CHIP_H - 4}>
                        <input xmlns="http://www.w3.org/1999/xhtml"
                          value={m.destination_label}
                          onChange={(e) => onMappingChange(si, fi, "destination_label", e.target.value)}
                          placeholder="dest.field"
                          style={{
                            width: "100%", height: "100%", border: "none", outline: "none",
                            background: "transparent", fontSize: 11, fontWeight: 700, color: "#111827",
                            fontFamily: "ui-monospace, SFMono-Regular, monospace",
                            paddingLeft: 8, paddingRight: 4, boxSizing: "border-box"
                          }} />
                      </foreignObject>

                      {/* × delete row */}
                      <foreignObject x={CHIP_R_X + CHIP_W + 6} y={ry + 3} width={22} height={22}>
                        <button xmlns="http://www.w3.org/1999/xhtml"
                          onClick={() => onDeleteMapping(si, fi)}
                          style={{
                            width: 22, height: 22, border: "none", background: "none",
                            cursor: "pointer", color: "#9ca3af", fontSize: 17, fontWeight: 700,
                            borderRadius: 4, padding: 0, lineHeight: 1
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = "#ef4444"; e.currentTarget.style.background = "#fff1f2"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = "#9ca3af"; e.currentTarget.style.background = "none"; }}>
                          ×
                        </button>
                      </foreignObject>
                    </g>
                  );
                })
              )}

              {/* + Add Field Mapping button — one per branch/section */}
              <foreignObject x={CHIP_L_X} y={startY + h - S_PAD_B - ADD_H} width={240} height={ADD_H}>
                <button xmlns="http://www.w3.org/1999/xhtml"
                  onClick={() => onAddMapping(si)}
                  style={{
                    height: ADD_H, border: "1.5px dashed #93c5fd", background: "#eff6ff",
                    borderRadius: 8, cursor: "pointer", fontSize: 11, fontWeight: 700,
                    color: "#2563eb", padding: "0 14px", display: "inline-flex",
                    alignItems: "center", gap: 6, whiteSpace: "nowrap"
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#dbeafe"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#eff6ff"; }}>
                  + Add Field Mapping
                </button>
              </foreignObject>
            </g>
          );
        })}
      </svg>

      {/* Platform picker overlay placed robustly using portals so it doesn't get cut off */}
      {pickerState && (() => {
        const editNode = platformNodes.find(n => n.id === pickerState.id);
        if (!editNode) return null;
        
        return ReactDOM.createPortal(
          <div className="fixed z-[99999]" style={{ top: Math.min(pickerState.y + 10, window.innerHeight - 300), left: pickerState.x - 120 }}>
            <PlatformPicker
              role={editNode.type}
              currentValue={editNode.label}
              onSelect={(v) => { onNodeChange(editNode.id, v); setPickerState(null); }}
              onClose={() => setPickerState(null)}
            />
          </div>,
          document.body
        );
      })()}
    </div>
  );
};

// ─── Node Chip (Nodes Bar) ────────────────────────────────────────────────────
const NodeChip = ({ node, onEdit, onDelete }) => {
  const colors = {
    trigger: { bg: "#fff7ed", border: "#fed7aa", text: "#c2410c", dot: "#f97316" },
    source: { bg: "#eff6ff", border: "#bfdbfe", text: "#1d4ed8", dot: "#3b82f6" },
    destination: { bg: "#f0fdf4", border: "#bbf7d0", text: "#15803d", dot: "#22c55e" },
    action: { bg: "#faf5ff", border: "#e9d5ff", text: "#7e22ce", dot: "#a855f7" },
  };
  const c = colors[node.type] || colors.destination;
  return (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border group shrink-0"
      style={{ background: c.bg, borderColor: c.border }}>
      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: c.dot }} />
      <div>
        <p className="text-[0.58rem] font-black uppercase tracking-widest leading-none" style={{ color: c.dot }}>{node.type}</p>
        <p className="text-[0.8rem] font-bold leading-tight" style={{ color: c.text }}>{node.label}</p>
      </div>
      <button onClick={onEdit} className="ml-1 p-0.5 rounded opacity-50 group-hover:opacity-100 transition" style={{ color: c.text }}>
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.232 5.232l3.536 3.536M9 13l-4 1 1-4L17.5 4a2.121 2.121 0 013 3L9 13z" />
        </svg>
      </button>
      <button onClick={onDelete} className="p-0.5 rounded opacity-0 group-hover:opacity-100 transition text-red-400 hover:text-red-600">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────
const DiagramEditModal = ({
  isOpen, onClose, onApply,
  currentFlow = {}, currentMappings = [],
  currentBranchMappings = null,  // null for 1:1; array-of-arrays for pipeline
  flowGraph = null,
}) => {
  const [platformNodes, setPlatformNodes] = useState([]);
  // branchMappings: array of arrays — index = branch/section index
  const [branchMappings, setBranchMappings] = useState([[]]);
  const [addNodeOpen, setAddNodeOpen] = useState(false);
  const [editNodeEvent, setEditNodeEvent] = useState(null); // { idx, x, y }
  const [isMinimized, setIsMinimized] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const addNodeRef = useRef();
  const origMappingsRef = useRef([]);
  const origNodes = useMemo(() => (flowGraph?.nodes || []).map((n) => ({ ...n })), [flowGraph]);

  useEffect(() => {
    if (!isOpen) return;
    let nodes = (flowGraph?.nodes || []).map((n) => ({ ...n }));
    if (nodes.length === 0) {
      const fb = [];
      if (currentFlow.trigger) fb.push({ id: "trigger", type: "trigger", label: currentFlow.trigger });
      if (currentFlow.source) fb.push({ id: "source", type: "source", label: currentFlow.source });
      if (currentFlow.destination) fb.push({ id: "destination", type: "destination", label: currentFlow.destination });
      nodes = fb;
    }
    setPlatformNodes(nodes);

    // Determine how many branches we'll need
    let numBranches = 1;
    if (flowGraph?.edges?.length > 0) {
      const inDeg = {}, outDeg = {};
      flowGraph.edges.forEach(e => {
         outDeg[e.from] = (outDeg[e.from] || 0) + 1;
         inDeg[e.to] = (inDeg[e.to] || 0) + 1;
      });
      const maxOut = Math.max(0, ...Object.values(outDeg));
      const maxIn = Math.max(0, ...Object.values(inDeg));
      if (maxOut <= 1 && maxIn <= 1 && flowGraph.edges.length > 1) {
         numBranches = flowGraph.edges.length; // PIPELINE
      } else {
        const lNodesInit = nodes.filter((n) => n.type !== "destination");
        const rNodesInit = nodes.filter((n) => n.type === "destination");
        const isSeq = lNodesInit.length === 2 && lNodesInit[0].type === "trigger" && (lNodesInit[1].type === "source" || lNodesInit[1].type === "action") && rNodesInit.length === 1;
        numBranches = isSeq ? 1 : Math.max(lNodesInit.length || 1, rNodesInit.length || 1);
      }
    } else {
      const lNodesInit = nodes.filter((n) => n.type !== "destination");
      const rNodesInit = nodes.filter((n) => n.type === "destination");
      const isSeq = lNodesInit.length === 2 && lNodesInit[0].type === "trigger" && (lNodesInit[1].type === "source" || lNodesInit[1].type === "action") && rNodesInit.length === 1;
      numBranches = isSeq ? 1 : Math.max(lNodesInit.length || 1, rNodesInit.length || 1);
    }

    let initial;
    if (Array.isArray(currentBranchMappings) && currentBranchMappings.length > 0) {
      // Pipeline (fan-out / fan-in) — use the per-branch mappings from the backend
      // Pad or trim to match numBranches
      initial = Array.from({ length: numBranches }, (_, i) =>
        (currentBranchMappings[i] || []).map(normalizeMapping)
      );
    } else {
      // 1:1 integration — put all mappings into branch 0
      const m = (currentMappings || []).map(normalizeMapping);
      initial = Array.from({ length: numBranches }, (_, i) => (i === 0 ? m : []));
    }
    setBranchMappings(initial);
    origMappingsRef.current = initial;

    console.log("[DiagramEdit] init nodes:", nodes, "branchMappings:", initial);
  }, [isOpen]); // eslint-disable-line

  // Recalculate branch count when nodes change
  useEffect(() => {
    setPlatformNodes((prev) => {
      let needed = 1;
      if (flowGraph?.edges?.length > 0) {
        const inDeg = {}, outDeg = {};
        flowGraph.edges.forEach(e => {
           outDeg[e.from] = (outDeg[e.from] || 0) + 1;
           inDeg[e.to] = (inDeg[e.to] || 0) + 1;
        });
        const maxOut = Math.max(0, ...Object.values(outDeg));
        const maxIn = Math.max(0, ...Object.values(inDeg));
        if (maxOut <= 1 && maxIn <= 1 && flowGraph.edges.length > 1) {
           needed = flowGraph.edges.length; // PIPELINE
        } else {
           const lNodesInit = prev.filter((n) => n.type !== "destination");
           const rNodesInit = prev.filter((n) => n.type === "destination");
           const isSeq = lNodesInit.length === 2 && lNodesInit[0].type === "trigger" && (lNodesInit[1].type === "source" || lNodesInit[1].type === "action") && rNodesInit.length === 1;
           needed = isSeq ? 1 : Math.max(lNodesInit.length || 1, rNodesInit.length || 1);
        }
      } else {
        const lNodesInit = prev.filter((n) => n.type !== "destination");
        const rNodesInit = prev.filter((n) => n.type === "destination");
        const isSeq = lNodesInit.length === 2 && lNodesInit[0].type === "trigger" && (lNodesInit[1].type === "source" || lNodesInit[1].type === "action") && rNodesInit.length === 1;
        needed = isSeq ? 1 : Math.max(lNodesInit.length || 1, rNodesInit.length || 1);
      }
      setBranchMappings((bm) => {
        if (bm.length === needed) return bm;
        if (bm.length < needed) return [...bm, ...Array.from({ length: needed - bm.length }, () => [])];
        return bm.slice(0, needed);
      });
      return prev;
    });
  }, [platformNodes.length]); // eslint-disable-line

  useEffect(() => {
    if (!isOpen) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!addNodeOpen) return;
    const h = (e) => { if (addNodeRef.current && !addNodeRef.current.contains(e.target)) setAddNodeOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [addNodeOpen]);

  // Per-branch mapping handlers
  const handleMappingChange = useCallback((branchIdx, rowIdx, field, value) =>
    setBranchMappings((bm) => {
      const next = bm.map((b) => [...b]);
      next[branchIdx] = next[branchIdx].map((m, i) => i === rowIdx ? { ...m, [field]: value } : m);
      return next;
    }), []);

  const handleDeleteMapping = useCallback((branchIdx, rowIdx) =>
    setBranchMappings((bm) => {
      const next = bm.map((b) => [...b]);
      next[branchIdx] = next[branchIdx].filter((_, i) => i !== rowIdx);
      return next;
    }), []);

  const handleAddMapping = useCallback((branchIdx) =>
    setBranchMappings((bm) => {
      const next = bm.map((b) => [...b]);
      if (!next[branchIdx]) next[branchIdx] = [];
      next[branchIdx] = [...next[branchIdx], { source_label: "", destination_label: "", transformation: "" }];
      return next;
    }), []);

  const handleNodeChange = useCallback((id, v) =>
    setPlatformNodes((p) => p.map((n) => n.id === id ? { ...n, label: v } : n)), []);
  const updateNodeAt = (idx, v) =>
    setPlatformNodes((p) => { const n = [...p]; n[idx] = { ...n[idx], label: v }; return n; });
  const deleteNodeAt = (idx) => setPlatformNodes((p) => p.filter((_, i) => i !== idx));
  const addNode = (type, label) => {
    setPlatformNodes((p) => [...p, { id: `${type}_${Date.now()}`, type, label }]);
    setAddNodeOpen(false);
  };

  const handleApply = () => {
    // ── 1. Node (platform) diffs ──────────────────────────────────────────────
    const nodeChanges = [];  // [{ branchIdx, from, to, type }]
    platformNodes.forEach((node, idx) => {
      const orig = origNodes.find((o) => o.id === node.id);
      if (orig && orig.label !== node.label) {
        // Find which branch this destination maps to
        const destNodes = platformNodes.filter((n) => n.type === "destination");
        const branchIdx = destNodes.findIndex((n) => n.id === node.id);
        nodeChanges.push({
          branchIdx: branchIdx >= 0 ? branchIdx : 0,
          type: node.type,
          from: orig.label,
          to: node.label,
        });
      }
    });
    // New nodes (added) — no original counterpart
    platformNodes
      .filter((n) => !origNodes.find((o) => o.id === n.id))
      .forEach((n) => nodeChanges.push({ branchIdx: -1, type: n.type, from: null, to: n.label }));

    // ── 2. Per-branch mapping diffs ───────────────────────────────────────────
    // Record added, modified, and removed rows for the update message
    const origBranches = origMappingsRef.current || [];  // snapshot captured on open
    const changedBranchMappings = branchMappings.map((branch, bi) => {
      const origBranch = origBranches[bi] || [];
      const origKeys = new Set(origBranch.map((m) => m.source_label?.trim()?.toLowerCase()));
      const currentKeys = new Set(branch.map((m) => m.source_label?.trim()?.toLowerCase()));

      const added = branch.filter(
        (m) =>
          m.source_label?.trim() &&
          m.destination_label?.trim() &&
          !origKeys.has(m.source_label.trim().toLowerCase())
      );

      const modified = branch
        .filter((m) => {
          if (!m.source_label?.trim() || !m.destination_label?.trim()) return false;
          const origRow = origBranch.find(
            (o) => o.source_label?.trim()?.toLowerCase() === m.source_label.trim().toLowerCase()
          );
          return origRow && origRow.destination_label?.trim() !== m.destination_label?.trim();
        })
        .map((m) => {
          const origRow = origBranch.find(
            (o) => o.source_label?.trim()?.toLowerCase() === m.source_label.trim().toLowerCase()
          );
          return { ...m, old_destination_label: origRow.destination_label };
        });

      const removed = origBranch.filter(
        (m) => m.source_label?.trim() && !currentKeys.has(m.source_label.trim().toLowerCase())
      );

      return { added, modified, removed };
    });

    const hasNodeChanges = nodeChanges.length > 0;
    const hasMappingChanges = changedBranchMappings.some((b) => b.added.length > 0 || b.modified.length > 0 || b.removed.length > 0);

    if (!hasNodeChanges && !hasMappingChanges) { onClose(); return; }

    onApply({
      nodeChanges,           // [{ branchIdx, type, from, to }]
      changedBranchMappings, // per-branch arrays of only NEW/CHANGED rows
      updatedNodes: platformNodes,
      branchMappings,        // full current state for reference
    });
    onClose();
  };

  if (!isOpen) return null;

  const TYPE_ORDER = { trigger: 0, source: 1, action: 2, destination: 3 };
  const sortedNodes = [...platformNodes].sort((a, b) => (TYPE_ORDER[a.type] ?? 9) - (TYPE_ORDER[b.type] ?? 9));
  const mCount = branchMappings.flat().filter((m) => m.source_label && m.destination_label).length;

  const layoutEdges = flowGraph?.edges?.length > 0 ? flowGraph.edges : null;
  let isPipeline = false;
  if (layoutEdges) {
    const inDeg = {}, outDeg = {};
    layoutEdges.forEach(e => {
       outDeg[e.from] = (outDeg[e.from] || 0) + 1;
       inDeg[e.to] = (inDeg[e.to] || 0) + 1;
    });
    const maxOut = Math.max(0, ...Object.values(outDeg));
    const maxIn = Math.max(0, ...Object.values(inDeg));
    if (maxOut <= 1 && maxIn <= 1 && layoutEdges.length > 1) {
       isPipeline = true;
    }
  }

  // Layout type label for header
  const lNodes = sortedNodes.filter((n) => n.type !== "destination");
  const rNodes = sortedNodes.filter((n) => n.type === "destination");
  const isSeq = !isPipeline && lNodes.length === 2 && lNodes[0].type === "trigger" && (lNodes[1].type === "source" || lNodes[1].type === "action") && rNodes.length === 1;

  const layoutLabel = isPipeline
    ? `Pipeline Flow (${layoutEdges.length} mappings)`
    : isSeq
      ? `Sequential 1:1 Flow (${lNodes[0]?.label} → ${lNodes[1]?.label} → ${rNodes[0]?.label})`
      : lNodes.length === 1 && rNodes.length > 1
        ? `Fan-Out  (${lNodes[0]?.label} → ${rNodes.length} destinations)`
        : lNodes.length > 1 && rNodes.length === 1
          ? `Fan-In  (${lNodes.length} sources → ${rNodes[0]?.label})`
          : lNodes.length > 0 && rNodes.length > 0
            ? `${lNodes.map((n) => n.label).join(", ")} → ${rNodes.map((n) => n.label).join(", ")}`
            : "";

  const modal = (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center p-4"
      style={{ background: "rgba(10,15,30,0.65)", backdropFilter: "blur(8px)", display: isMinimized ? "none" : "flex" }}
      onClick={onClose}>
      <div className="relative flex flex-col bg-white rounded-2xl overflow-hidden shadow-2xl transition-all duration-300"
        style={{
          width: isFullscreen ? "100vw" : "min(96vw, 940px)", 
          height: isFullscreen ? "100vh" : "min(92vh, 900px)",
          borderRadius: isFullscreen ? "0" : "1rem",
        }}
        onClick={(e) => e.stopPropagation()}>

        {/* ── Title bar (no Cancel/Apply here — only in footer) ── */}
        <div className="flex items-center justify-between px-5 py-3.5 shrink-0 relative"
          style={{ background: "linear-gradient(135deg,#1e293b,#0f172a)" }}>
          <div className="flex items-center gap-2 z-10 group">
            {/* Close */}
            <button onClick={onClose} className="w-3.5 h-3.5 rounded-full bg-red-500 hover:bg-red-400 flex items-center justify-center transition" title="Close">
              <span className="text-[10px] text-red-900 font-bold flex leading-none">×</span>
            </button>
            {/* Minimize */}
            <button onClick={() => setIsMinimized(true)} className="w-3.5 h-3.5 rounded-full bg-yellow-400 hover:bg-yellow-300 flex items-center justify-center transition" title="Minimize">
              <span className="text-[10px] text-yellow-900 font-bold flex leading-none">−</span>
            </button>
            {/* Fullscreen */}
            <button onClick={() => setIsFullscreen(!isFullscreen)} className="w-3.5 h-3.5 rounded-full bg-green-500 hover:bg-green-400 flex items-center justify-center transition" title="Fullscreen">
              <svg className="w-1.5 h-1.5 text-green-900" fill="currentColor" viewBox="0 0 16 16"><path d="M1.5 1.5v5h2v-3h3v-2h-5zM14.5 1.5h-5v2h3v3h2v-5zM14.5 14.5v-5h-2v3h-3v2h5zM1.5 14.5h5v-2h-3v-3h-2v5z"/></svg>
            </button>
          </div>
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="text-[0.78rem] font-bold text-white/75 select-none tracking-wide">
              ✎ Edit Integration — Data Mapping
            </span>
          </div>
          {/* Spacer (mirror of left buttons for centering) */}
          <div className="w-[78px]" />
        </div>

        {/* ── Sub-header ── */}
        <div className="flex items-center justify-between px-5 py-2 bg-white border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[0.85rem] font-bold text-gray-800 shrink-0">Data Mapping</span>
            {layoutLabel && (
              <span className="text-[0.7rem] text-gray-400 font-medium truncate">— {layoutLabel}</span>
            )}
          </div>
        </div>

        {/* ── Nodes bar ── */}
        <div className="flex items-center gap-2 px-5 py-2.5 bg-gray-50/80 border-b border-gray-100 shrink-0 flex-wrap relative">
          <span className="text-[0.6rem] font-black text-gray-400 uppercase tracking-widest mr-1 shrink-0">Nodes</span>
          {sortedNodes.map((node, i) => {
            const origIdx = platformNodes.indexOf(node);
            return (
              <div key={node.id || i} className="relative">
                <NodeChip node={node}
                  onEdit={(e) => setEditNodeEvent({ idx: origIdx, x: e.clientX, y: e.clientY })}
                  onDelete={() => deleteNodeAt(origIdx)} />
              </div>
            );
          })}
          {editNodeEvent && (() => {
            const node = platformNodes[editNodeEvent.idx];
            if (!node) return null;
            return ReactDOM.createPortal(
              <div className="fixed z-[99999]" style={{ top: editNodeEvent.y + 10, left: editNodeEvent.x }}>
                <PlatformPicker role={node.type} currentValue={node.label}
                  onSelect={(v) => { updateNodeAt(editNodeEvent.idx, v); setEditNodeEvent(null); }}
                  onClose={() => setEditNodeEvent(null)} />
              </div>,
              document.body
            );
          })()}
          <div className="relative shrink-0" ref={addNodeRef}>
            <button onClick={() => setAddNodeOpen((v) => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[0.72rem] font-bold text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-50 transition">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
              </svg>
              Add Node
            </button>
            {addNodeOpen && (
              <div className="absolute top-full mt-1 left-0 z-50 bg-white rounded-xl overflow-hidden"
                style={{ width: 260, boxShadow: "0 8px 30px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.07)" }}>
                <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                  <p className="text-[0.65rem] font-black uppercase tracking-widest text-gray-500">Add Platform Node</p>
                </div>
                <div className="max-h-64 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
                  {[
                    { title: "Trigger", role: "trigger", opts: ROLE_OPTIONS["trigger"] },
                    { title: "Connector", role: "destination", opts: ALL_CONNECTORS }
                  ].map(({ title, role, opts }) => (
                    <div key={title}>
                      <div className="px-4 py-1.5 text-[0.58rem] font-black text-gray-300 uppercase tracking-widest bg-gray-50/70 border-b border-gray-100 capitalize">{title}</div>
                      {(opts || []).map((p) => (
                        <button key={p.value} onClick={() => addNode(role, p.value)}
                          className="w-full text-left px-4 py-2 flex items-center border-b border-gray-50 hover:bg-gray-50 transition">
                          <p className="text-[0.78rem] font-bold text-gray-900">{p.value}</p>
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Canvas ── */}
        <div className="flex-1 flex overflow-auto bg-white px-3 py-3"
          style={{ scrollbarWidth: "thin", scrollbarColor: "#cbd5e1 transparent" }}>
          {platformNodes.length === 0 ? (
            <div className="m-auto flex flex-col items-center justify-center text-center">
              <p className="text-[0.85rem] text-gray-500 font-semibold">No nodes yet</p>
              <p className="text-[0.75rem] text-gray-400 mt-1">Use "+ Add Node" above to add platforms</p>
            </div>
          ) : (
            <div className="m-auto w-full max-w-full">
              <FlowCanvas
                platformNodes={platformNodes}
                layoutEdges={layoutEdges}
                branchMappings={branchMappings}
                onMappingChange={handleMappingChange}
                onDeleteMapping={handleDeleteMapping}
                onAddMapping={handleAddMapping}
                onNodeChange={handleNodeChange}
              />
            </div>
          )}
        </div>

        {/* ── Footer (single Cancel + Apply Changes — no duplicates) ── */}
        <div className="flex items-center justify-between px-5 py-3.5 bg-gray-50 border-t border-gray-200 shrink-0">
          <span className="text-[0.67rem] text-gray-800 font-medium">
            {mCount} mapped field{mCount !== 1 ? "s" : ""} · {platformNodes.length} node{platformNodes.length !== 1 ? "s" : ""}
          </span>
          <div className="flex items-center gap-2 ml-4">
            <button onClick={onClose} className="px-4 py-2 text-[0.8rem] font-semibold text-gray-500 hover:text-gray-700 transition">Cancel</button>
            <button onClick={handleApply}
              className="flex items-center gap-2 px-5 py-2 text-[0.82rem] font-extrabold text-white bg-blue-600 rounded-xl hover:bg-blue-700 shadow transition active:scale-[0.97]">
              Apply Changes
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isMinimized && (
        <div className="fixed bottom-6 left-6 z-[9999] bg-white rounded-xl shadow-2xl p-4 flex items-center gap-4 border border-gray-200">
          <p className="text-sm font-bold text-gray-800">Integration Editor Minimized</p>
          <button onClick={() => setIsMinimized(false)} className="px-4 py-1.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-lg shadow">Restore</button>
        </div>
      )}
      {!isMinimized && ReactDOM.createPortal(modal, document.body)}
    </>
  );
};

export default DiagramEditModal;
